/**
 * Point Measure Tool
 *
 * Allows measuring individual point coordinates.
 * Each click places a single point and immediately finishes the measurement.
 */

import * as THREE from 'three';
import { MeasureToolBase } from './MeasureToolBase';
import { DragEventData } from '../../BaseTool';
import { MeasureTools } from '../../../utils/Measure';
import { PointAnnotation, PointoramaAnnotation } from '../../../utils/PointoramaAnnotation';

export class PointMeasurePersonaTool extends MeasureToolBase {
  readonly name = 'point_measure';
  readonly measureType = MeasureTools.POINT;

  protected createAnnotation(): PointoramaAnnotation {
    return new PointAnnotation({ viewer: this.viewer, name: this.viewer.translationService.t('point', { swc: true }) });
  }

  // --- Mouse event handlers ---

  public onMouseMove(event: MouseEvent): void {
    // BaseTool fires both onDrag and onMouseMove during drag — skip here
    if (this.isDraggingSphere()) return;

    const mouse = this.getMouseFromEvent(event);

    // Update hover state
    this.updateHoverState();

    // Update preview point position
    this.drawNewMeasure(mouse);

    // Update visuals
    this.updateVisuals();
  }

  // --- Mouse drag handlers (BaseTool drag system) ---

  public onDragStart(data: DragEventData): void {
    if (data.button !== 0) return; // left button only

    this.onFirstDragDetected();
  }

  public onDrag(data: DragEventData): void {
    if (!this.draggedSphere) return;

    this.setHoveredLineIntersect();
    this.setHoveredSphere();

    const mouse = this.getMouseFromEvent(data.event);
    const location = this.getPointUnderMouse(mouse);
    if (location) {
      this.onDragPoint(location);
    }

    this.updateVisuals();
  }

  public onDragEnd(_data: DragEventData): void {
    if (!this.draggedSphere) return;
    this.draggedSphere.unHighlight();
    this.onDragPointEnd();
  }

  public onClick(_event: MouseEvent): void {
    // Plane click handling (3D projection planes)
    if (this.planeSnapping.getHoveredPlane()) {
      this.planeSnapping.onClick(THREE.MOUSE.LEFT);
      this.planeSnapping.updateHover();
      return;
    }

    if (!this.canPlacePoint()) return;

    // Finish immediately and start a new one (single-point measurement)
    this.viewer.scene.finishMeasurement(this.currentMeasure);
    this.startMeasure();
  }

  public onRightClick(_event: MouseEvent): void {
    // Right-click on existing sphere: delete the point
    // (hovered sphere can be stale/detached until the next mouse move)
    if (this.hoveredSphere?.parent && !this.drawingInProgress()) {
      const measure = this.hoveredSphere.parent as PointoramaAnnotation;
      const pointIndex = measure.getSphereIndex(this.hoveredSphere);
      this.removeMarker(measure, pointIndex);
    }
  }
}
